
import React, { useMemo, useState } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { PAYMENT_URLS } from "@/components/pricing/pricingConstants";
import { useAuth } from "@/contexts/auth";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { AreaResult } from "@/types/supabase";
import { Check, CheckCircle2, ChevronDown, MapPinned, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { getGroupSelectionState, getGroupedSelectionChips } from "@/utils/areaSelection";
import { areaPillClassName, areaPillRemoveClassName } from "@/components/shared/selectionPillStyles";

interface RHFLikeForm {
  control: Control<FieldValues>;
}

interface LocationSectionProps {
  form: RHFLikeForm;
  isPremium?: boolean;
  isAdmin?: boolean;
  areas?: AreaResult[];
  isDisabled?: boolean;
  selectedSuburbs?: string[];
  handleSuburbSelect?: (suburb: string) => void;
  handleSuburbBulkSelect?: (suburbs: string[]) => void;
  handleRemoveSuburb?: (suburb: string) => void;
  handleSuburbBulkRemove?: (suburbs: string[]) => void;
  isWhatsAppVerified?: boolean;
  areasGrouped?: Record<string, AreaResult[]>;
}

const LocationSection = ({
  form,
  isPremium = false,
  isAdmin = false,
  isDisabled = false,
  selectedSuburbs = [],
  handleSuburbSelect,
  handleSuburbBulkSelect,
  handleRemoveSuburb,
  handleSuburbBulkRemove,
  isWhatsAppVerified = false,
  areasGrouped = {}
}: LocationSectionProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pickerOpen, setPickerOpen] = useState(false);

  const handlePremiumUpgrade = () => {
    if (user) {
      navigate(PAYMENT_URLS.premium_tier);
    } else {
      navigate(`/auth?tab=signup&redirect=${encodeURIComponent(PAYMENT_URLS.premium_tier)}`);
    }
  };

  // Max areas allowed based on subscription
  const getMaxAreasAllowed = () => {
    if (isPremium || isAdmin) return Infinity;
    if (isWhatsAppVerified) return 4;
    return 2; // Free tier
  };
  
  const maxAreasAllowed = getMaxAreasAllowed();
  const atMaxAreas = selectedSuburbs.length >= maxAreasAllowed && maxAreasAllowed !== Infinity;
  const canBulkSelectTerritories = isPremium || isAdmin;

  const groupedAreaNames = useMemo(
    () => Object.fromEntries(
      Object.entries(areasGrouped).map(([territory, territoryAreas]) => [
        territory,
        territoryAreas.map((area) => area.name),
      ])
    ),
    [areasGrouped]
  );

  const selectedChips = useMemo(
    () => getGroupedSelectionChips(canBulkSelectTerritories ? groupedAreaNames : {}, selectedSuburbs),
    [canBulkSelectTerritories, groupedAreaNames, selectedSuburbs]
  );

  const handleTerritoryToggle = (territory: string, territoryAreas: AreaResult[]) => {
    if (!canBulkSelectTerritories || !handleSuburbBulkSelect || !handleSuburbBulkRemove) {
      return;
    }

    const territoryNames = territoryAreas.map((area) => area.name);
    const selectionState = getGroupSelectionState(territoryNames, selectedSuburbs);

    if (selectionState === "all") {
      handleSuburbBulkRemove(territoryNames);
      return;
    }

    handleSuburbBulkSelect(territoryNames);
  };
  
  return (
    <div className="space-y-6 border-t border-brand-cloud pt-4">
      <div>
        <h3 className="public-card-title">Location Information</h3>
      </div>
      
      {/* Areas Serviced */}
      <FormField 
        control={form.control} 
        name="suburbs"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between">
              <FormLabel>Areas Serviced</FormLabel>
              {!isPremium && !isAdmin && atMaxAreas && (
                <p className="text-sm text-amber-600">
                  {isWhatsAppVerified ? "WhatsApp Verified includes up to 4 areas." : "Starter includes up to 2 areas."}
                  <Button variant="link" onClick={handlePremiumUpgrade} className="text-amber-700 h-auto p-0 ml-1">
                    Upgrade to Premium
                  </Button>
                </p>
              )}
            </div>

            {/* Display selected suburbs above the dropdown */}
            {selectedChips.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                {selectedChips.map((chip) => {
                  return (
                    <Badge key={chip.key} variant="secondary" className={areaPillClassName}>
                      <span>{chip.label}</span>
                      {!isDisabled && (
                        <button 
                          type="button" 
                          onClick={() => {
                            if (chip.type === "group" && handleSuburbBulkRemove) {
                              handleSuburbBulkRemove(chip.values);
                              return;
                            }

                            if (handleRemoveSuburb) {
                              handleRemoveSuburb(chip.values[0]);
                            }
                          }} 
                          className={`ml-1 ${areaPillRemoveClassName}`}
                        >
                          ×
                        </button>
                      )}
                    </Badge>
                  );
                })}
              </div>
            )}

            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    disabled={isDisabled}
                    className="w-full justify-between"
                  >
                    <span className="flex items-center gap-2 truncate">
                      <MapPinned className="h-4 w-4 text-muted-foreground" />
                      {selectedSuburbs.length > 0 ? `${selectedSuburbs.length} area${selectedSuburbs.length === 1 ? "" : "s"} selected` : "Search and select areas"}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="w-[360px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search territories or areas..." />
                  <CommandList>
                    <CommandEmpty>No matching areas found.</CommandEmpty>
                    {Object.entries(areasGrouped).map(([territory, territoryAreas]) => {
                      const territoryNames = territoryAreas.map((area) => area.name);
                      const territoryState = getGroupSelectionState(territoryNames, selectedSuburbs);

                      return (
                        <CommandGroup key={territory} heading={territory}>
                          {canBulkSelectTerritories && (
                            <CommandItem
                              value={`all ${territory} ${territoryNames.join(" ")}`}
                              onSelect={() => handleTerritoryToggle(territory, territoryAreas)}
                              disabled={isDisabled}
                              className="font-medium"
                            >
                              {territoryState === "all" ? (
                                <CheckCircle2 className="mr-2 h-4 w-4 text-whatsapp" />
                              ) : territoryState === "partial" ? (
                                <Check className="mr-2 h-4 w-4 text-whatsapp" />
                              ) : (
                                <Plus className="mr-2 h-4 w-4 text-muted-foreground" />
                              )}
                              <span>
                                {territoryState === "all" ? `Clear ${territory}` : `Select all in ${territory}`}
                              </span>
                            </CommandItem>
                          )}
                          {territoryAreas.map((area) => {
                            const isSelected = selectedSuburbs.includes(area.name);
                            const isDisabledItem = !isSelected && atMaxAreas && !isPremium && !isAdmin;

                            return (
                              <CommandItem
                                key={`${territory}-${area.name}`}
                                value={`${area.name} ${territory}`}
                                onSelect={() => {
                                  if (isSelected) {
                                    handleRemoveSuburb?.(area.name);
                                  } else if (!isDisabledItem) {
                                    handleSuburbSelect?.(area.name);
                                  }
                                }}
                                disabled={isDisabled || isDisabledItem}
                                className={cn(isSelected && "text-whatsapp font-medium")}
                              >
                                <CheckCircle2 className={cn("mr-2 h-4 w-4", isSelected ? "text-whatsapp opacity-100" : "opacity-0")} />
                                <span className={cn(isDisabledItem && "text-brand-slate/60")}>{area.name}</span>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      );
                    })}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <p className="mt-1 public-small">Where do you provide your services?</p>
            {!canBulkSelectTerritories && (
              <p className="mt-1 public-small">Premium subscriptions can select an entire territory in one step.</p>
            )}
            <FormMessage />
          </FormItem>
        )} 
      />
    </div>
  );
};

export default LocationSection;
